import { useEffect, useMemo, useRef, useState } from "react";
import { GridColDef } from "@mui/x-data-grid";
import {
  IVisitor,
  IVisitorGridFilter,
  IVisitorGridFilterSchema,
} from "../api/types";
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Grid,
  Typography,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { formatDateTimeIST } from "utils/date";
import { useForm } from "react-hook-form";
import { IDropdownModel } from "common/models/IDropdownModel";
import { GetMemberTypeValues } from "features/memberType/api/hooks";
import useDashboardStore from "../store/DashboardStore";
import { GetVisitors } from "../api/hooks";
import { zodResolver } from "@hookform/resolvers/zod";
import { SelectField } from "components/@extended/SelectField";
import StyledDataGrid from "components/@extended/StyledDataGrid";

const VisitorsGrid = () => {
  const {
    visitorPostModel,
    handleVisitorFiltering,
    handleVisitorPagination,
    handleVisitorSorting,
  } = useDashboardStore();

  const { data: getVisitors, isFetching } = GetVisitors(visitorPostModel);

  const columns: GridColDef<IVisitor>[] = useMemo(
    () => [
      {
        field: "libraryName",
        headerName: "Library Name",
        flex: 1,
      },
      {
        field: "date",
        headerName: "Date",
        flex: 1,
        headerAlign: "center",
        align: "center",
        renderCell: (params) => {
          return params.value !== null
            ? formatDateTimeIST(params.row.date)
            : "-";
        },
      },
      {
        field: "todaysVisitor",
        headerName: "Todays Visitor",
        flex: 1,
        headerAlign: "center",
        align: "center",
      },
      {
        field: "weekVisitor",
        headerName: "Week Visitor",
        flex: 1,
        headerAlign: "center",
        align: "center",
        renderCell: (params) => {
          return params.row.weekVisitor ?? 0;
        },
      },
      {
        field: "monthVisitor",
        headerName: "Month Visitor",
        flex: 1,
        headerAlign: "center",
        align: "center",
        renderCell: (params) => {
          return params.row.monthVisitor ?? 0;
        },
      },
    ],
    []
  );

  const methods = useForm<IVisitorGridFilter>({
    mode: "all",
    resolver: zodResolver(IVisitorGridFilterSchema),
  });

  const { reset, handleSubmit, control } = methods;

  const onSubmit = handleSubmit(async (data) => {
    handleVisitorFiltering(data);
  });

  const { data: memberTypeQuery } = GetMemberTypeValues();
  const [memberTypeOptions, setMemberTypeOptions] = useState<IDropdownModel[]>(
    []
  );
  useEffect(() => {
    setMemberTypeOptions(memberTypeQuery?.apiData || []);
  }, [memberTypeQuery]);

  const handleClear = () => {
    handleVisitorFiltering({
      memberTypeID: null,
    });
  };

  useEffect(() => {
    reset(visitorPostModel.filterModel);
  }, [visitorPostModel.filterModel, reset]);

  const rowCountRef = useRef(getVisitors?.apiData?.count || 0);
  const rowCount = useMemo(() => {
    if (getVisitors?.apiData?.count !== undefined) {
      rowCountRef.current = getVisitors?.apiData?.count;
    }
    return rowCountRef.current;
  }, [getVisitors?.apiData?.count]);

  return (
    <>
      <Box component="form" onSubmit={onSubmit}>
        <Card elevation={3} sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h3" component={"h3"} marginBlockEnd={2}>
              Visitors
              <Divider sx={{ my: 1 }} />
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} md={4} lg={3}>
                <SelectField
                  options={memberTypeOptions}
                  control={control}
                  name="memberTypeID"
                  label="Member Type"
                  placeholder="Select Member Type"
                />
              </Grid>
            </Grid>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4} lg={3} sx={{ mt: 3 }}>
                <LoadingButton variant="contained" sx={{ mr: 1 }} type="submit" loading={isFetching} disabled={isFetching}>Search</LoadingButton>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={handleClear}
                 disabled={isFetching} >
                  Clear
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>

      <Grid container direction={"row"} spacing={2}>
        <Grid item xs={12} md={12}>
          <Card elevation={3} sx={{ p: 3 }}>
            <Grid container sx={{ height: 450 }}>
              <StyledDataGrid
                sx={{
                  "& .MuiDataGrid-columnHeaderTitle": {
                    whiteSpace: "wrap",
                    fontWeight: 600,
                    lineHeight: "normal",
                    fontSize: "13px",
                  },
                  "& .MuiDataGrid-cell": {
                    whiteSpace: "wrap",
                    alignContent: "center",
                    fontSize: 14,
                  },
                }}
                density="compact"
                disableRowSelectionOnClick
                getRowId={(item) => item.uniqueID}
                columns={columns}
                pageSizeOptions={[10, 20, 50, 100]}
                initialState={{
                  pagination: {
                    paginationModel: { ...visitorPostModel.pagingModel },
                  },
                }}
                sortModel={visitorPostModel.sortModel}
                onSortModelChange={handleVisitorSorting}
                onPaginationModelChange={handleVisitorPagination}
                getRowHeight={() => "auto"}
                paginationMode="server"
                sortingMode="server"
                rows={getVisitors?.apiData?.data}
                rowCount={rowCount}
                disableColumnFilter
                disableColumnSorting
                loading={isFetching}
              />
            </Grid>
          </Card>
        </Grid>
      </Grid>
    </>
  );
};

export default VisitorsGrid;
